import qs from "qs";
import { GetData } from "./Functions/SettingData";

// API module for the "All Battery Packs" inventory tab (packConfigMaster registry).
// Endpoints are documented in all-battery-packs-tab-spec.md.

const repeatArrayParams = (params) =>
    qs.stringify(params, { arrayFormat: "repeat" });

// GET /api/battery-packs — paginated registry list. Search values are folded into the
// `filters` JSON keyed by the selected search column, matching the other inventory tabs.
export const getBatteryPackList = async (data) => {
    const rawJson = {
        pageCount: data.rowsPerPage,
        pageNum: data.pageNumber,
        sortBy: data?.sortBy || "customerPackID",
        order: data?.orderBy || "asc",
    };

    if (data.filters && Object.keys(data.filters).length > 0) {
        rawJson.filters = JSON.stringify(data.filters);
    }

    const config = {
        method: "get",
        url: `/api/battery-packs`,
        headers: {
            "Content-Type": "application/json",
        },
        params: rawJson,
        paramsSerializer: repeatArrayParams,
    };

    return GetData(config, true);
};

// POST /api/battery-packs/list-action — download-csv | mail. Sends the same filters and
// sort as the list so the export matches what is on screen.
export const batteryPackListActions = async (data) => {
    const config = {
        method: "post",
        url: `/api/battery-packs/list-action`,
        headers: {
            "Content-Type": "application/json",
        },
        data,
    };

    return GetData(config, true);
};

// GET /api/battery-packs/search-suggestions — distinct values of one searchable column.
// columnName: customerPackID | deviceID | vin | licensePlateNo. Fewer than 2 characters
// returns an empty list from the backend, so no client-side guard is needed.
//
// Signature is (search, columnName, limit) because GenericDraggableTable's shared
// DeviceSearchInput calls searchSuggestionsHandler(typedValue, columnName, 5) positionally
// — do not reorder these.
export const batteryPackSearchSuggestions = async (
    search,
    columnName = "customerPackID",
    limit = 5
) => {
    const config = {
        method: "get",
        url: `/api/battery-packs/search-suggestions`,
        headers: {
            "Content-Type": "application/json",
        },
        params: {
            columnName,
            columnSearch: search,
            columnLimit: limit,
        },
        paramsSerializer: repeatArrayParams,
    };

    return GetData(config);
};

// Same endpoint, install-state filtered for the Record Replacement serial fields.
//   incoming — every registered serial, INCLUDING ones already installed elsewhere.
//              They are offered deliberately: hiding them left a technician unable to
//              tell an unknown serial from a taken one. The form refuses a taken one on
//              selection, naming the vehicle it sits on.
//   outgoing — serials free anywhere, OR already installed on THIS battery
// `mode` is only valid with columnName=customerPackID and requires batteryID; the backend
// 400s otherwise rather than silently returning the unfiltered list.
export const getPackSerialSuggestions = async (
    batteryID,
    { mode, search, limit = 10 } = {}
) => {
    const config = {
        method: "get",
        url: `/api/battery-packs/search-suggestions`,
        headers: {
            "Content-Type": "application/json",
        },
        params: {
            columnName: "customerPackID",
            columnSearch: search,
            columnLimit: limit,
            batteryID,
            mode,
        },
        paramsSerializer: repeatArrayParams,
    };

    return GetData(config);
};

// Resolve one serial's registry row (for the manufacturing date) by reusing the list
// endpoint's customerPackID filter. Deliberately a second call rather than widening the
// suggestions response: keeping that response a flat string array is what lets the shared
// DeviceSearchInput consumer keep working unchanged.
export const getBatteryPackBySerial = async (customerPackID) => {
    const config = {
        method: "get",
        url: `/api/battery-packs`,
        headers: {
            "Content-Type": "application/json",
        },
        params: {
            pageCount: 1,
            pageNum: 1,
            filters: JSON.stringify({ customerPackID: [customerPackID] }),
        },
        paramsSerializer: repeatArrayParams,
    };

    return GetData(config, true);
};
